/**
 * 舌头检测 — yolo1 pipeline
 *
 * 使用共享 MediaPipe 人脸检测（mediapipeFace.js），
 * Worker 中运行 ONNX EfficientNet-B0 分类推理。
 *
 * 保持与 camera/index.vue 的兼容接口。
 */

import { getInstance, prepareDetectInput, detectFace, STATUS } from './mediapipeFace';

// ---- 分类模型参数 ----
var MODEL_INPUT_SHAPE = [1, 3, 224, 224];
var MEAN = [0.485, 0.456, 0.406];
var STD = [0.229, 0.224, 0.225];

// ---- 距离判断阈值（与 yolo1 face_pipeline.js 一致） ----
var MIN_MOUTH_CROP_HEIGHT = 200;

// ---- 状态机 ----
var _worker = null;
var _loaded = false;
var _loading = false;
var _loadPromise = null;
var _pendingClassify = null;
var _detecting = false;

// ---- Canvas 缓存 ----
var _preprocessCanvas = null;
var _preprocessCtx = null;
var _cropCanvas = null;
var _cropCtx = null;
var _fullRotateCanvas = null;

export { STATUS };

// ========== 工具函数 ==========

function log() {
  var args = ['[TongueDetector]'].concat(Array.prototype.slice.call(arguments));
  console.log.apply(console, args);
}

function time() {
  var d = new Date();
  return ('0' + d.getHours()).slice(-2) + ':' +
         ('0' + d.getMinutes()).slice(-2) + ':' +
         ('0' + d.getSeconds()).slice(-2);
}

// ========== 模型加载 ==========

function loadLocalBuffer(filename) {
  if (typeof window !== 'undefined' && window.readLocalModel) {
    return window.readLocalModel(filename).then(function (buf) {
      log('本地加载 ' + filename + ', size=' + buf.byteLength);
      return buf;
    });
  }
  return Promise.reject(new Error('非本地环境'));
}

function fetchFileBuffer(url, label) {
  log('Web 加载 ' + label + '...');
  return fetch(url).then(function (res) {
    if (!res.ok) throw new Error(label + ' 加载失败: ' + res.status);
    return res.arrayBuffer();
  });
}

function fetchModelBuffer() {
  return loadLocalBuffer('zhui_an.onnx').catch(function () {
    return fetchFileBuffer('./lib/zhui_an.onnx', 'zhui_an.onnx');
  });
}

function fetchWasmBuffer() {
  return loadLocalBuffer('ort-wasm.wasm').catch(function () {
    return fetchFileBuffer('./lib/ort/ort-wasm.wasm', 'ort-wasm.wasm');
  }).catch(function () {
    log('WASM buffer 获取失败，Worker 将自行 fetch');
    return null;
  });
}

// ========== Canvas 管理 ==========

function ensurePreprocessCanvas() {
  if (!_preprocessCanvas) {
    _preprocessCanvas = document.createElement('canvas');
    _preprocessCanvas.width = MODEL_INPUT_SHAPE[2];
    _preprocessCanvas.height = MODEL_INPUT_SHAPE[3];
    _preprocessCtx = _preprocessCanvas.getContext('2d', { willReadFrequently: true });
  }
}

function ensureCropCanvas(w, h) {
  if (!_cropCanvas) {
    _cropCanvas = document.createElement('canvas');
    _cropCtx = _cropCanvas.getContext('2d');
  }
  _cropCanvas.width = w;
  _cropCanvas.height = h;
}

// ========== 全分辨率旋转（用于裁剪） ==========

function prepareFullResInput(source, rotation) {
  var sw = source.width || source.videoWidth || 0;
  var sh = source.height || source.videoHeight || 0;
  if (!sw || !sh) return source;

  if (!rotation || rotation === 1) return source;

  var rotW = (rotation === 2 || rotation === 3) ? sh : sw;
  var rotH = (rotation === 2 || rotation === 3) ? sw : sh;

  if (!_fullRotateCanvas) {
    _fullRotateCanvas = document.createElement('canvas');
  }
  _fullRotateCanvas.width = rotW;
  _fullRotateCanvas.height = rotH;

  var ctx = _fullRotateCanvas.getContext('2d');
  ctx.save();
  var halfW = rotW / 2;
  var halfH = rotH / 2;
  ctx.translate(halfW, halfH);
  if (rotation === 2) ctx.rotate(90 * Math.PI / 180);
  else if (rotation === 3) ctx.rotate(-90 * Math.PI / 180);
  else if (rotation === 4) ctx.rotate(180 * Math.PI / 180);
  ctx.drawImage(source, -sw / 2, -sh / 2);
  ctx.restore();

  return _fullRotateCanvas;
}

// ========== 眼角度校正（与 yolo1 face_pipeline.js 一致） ==========

/**
 * 计算两眼连线与水平线的夹角（度）
 * landmarks: [右眼, 左眼, 鼻尖, 口中心, 右耳, 左耳]
 */
function getEyeAngle(landmarks, imgW, imgH) {
  if (!landmarks || landmarks.length < 2) return 0;
  var rightEye = landmarks[0];
  var leftEye = landmarks[1];
  var dx = leftEye.x * imgW - rightEye.x * imgW;
  var dy = leftEye.y * imgH - rightEye.y * imgH;
  return Math.atan2(dy, dx) * (180 / Math.PI);
}

/**
 * 在 canvas 上旋转图像（与 yolo1 rotateImageOnCanvas 一致）
 * @returns {HTMLCanvasElement}
 */
function rotateImageOnCanvas(source, angle) {
  var srcW = source.naturalWidth || source.width;
  var srcH = source.naturalHeight || source.height;
  var canvas = document.createElement('canvas');
  canvas.width = srcW;
  canvas.height = srcH;
  var ctx = canvas.getContext('2d');
  ctx.translate(srcW / 2, srcH / 2);
  ctx.rotate((angle * Math.PI) / 180);
  ctx.drawImage(source, -srcW / 2, -srcH / 2, srcW, srcH);
  return canvas;
}

// ========== 口部裁剪 ==========

function cropMouthRegion(source, detection, procW, procH) {
  var bbox = detection.boundingBox;
  var landmarks = detection.landmarks;
  if (!landmarks || landmarks.length < 4) return null;

  var faceLeft   = Math.floor((bbox.xCenter - bbox.width / 2) * procW);
  var faceTop    = Math.floor((bbox.yCenter - bbox.height / 2) * procH);
  var faceRight  = Math.floor((bbox.xCenter + bbox.width / 2) * procW);
  var faceBottom = Math.floor((bbox.yCenter + bbox.height / 2) * procH);

  var mouthX = Math.round(landmarks[3].x * procW);
  var mouthY = Math.round(landmarks[3].y * procH);

  // 检查口部中心是否在人脸框内（与 yolo1 一致）
  if (mouthX < faceLeft || mouthX > faceRight || mouthY < faceTop || mouthY > faceBottom) {
    return null; // 口部中心超出人脸框范围
  }

  var estMouthWidth = (faceRight - faceLeft) * 0.3;
  var heightSize = Math.round(estMouthWidth * 1.4);
  heightSize = Math.max(200, Math.min(heightSize, 800));
  var widthSize = heightSize * 2;
  var offsetDown = Math.round(heightSize * 0.2);

  var cropX1 = mouthX - Math.floor(widthSize / 2);
  var cropY1 = mouthY - Math.floor(heightSize / 2) + offsetDown;
  var cropX2 = cropX1 + widthSize;
  var cropY2 = cropY1 + heightSize;

  cropX1 = Math.max(faceLeft, cropX1);
  cropY1 = Math.max(faceTop, cropY1);
  cropX2 = Math.min(faceRight, cropX2);
  cropY2 = Math.min(faceBottom, cropY2);

  if (cropX1 >= cropX2 || cropY1 >= cropY2) return null;

  var cropW = cropX2 - cropX1;
  var cropH = cropY2 - cropY1;

  if (cropH < MIN_MOUTH_CROP_HEIGHT) {
    return { tooFar: true, cropH: cropH };
  }

  var resultW = cropH * 2;
  var resultH = cropH;
  ensureCropCanvas(resultW, resultH);

  _cropCtx.fillStyle = '#000000';
  _cropCtx.fillRect(0, 0, resultW, resultH);

  var dstX = Math.floor((resultW - cropW) / 2);
  var dstY = Math.floor((resultH - cropH) / 2);

  _cropCtx.drawImage(source, cropX1, cropY1, cropW, cropH, dstX, dstY, cropW, cropH);

  return { canvas: _cropCanvas, cropH: cropH };
}

// ========== 分类预处理 ==========

function preprocessForClassifier(canvas) {
  ensurePreprocessCanvas();
  _preprocessCtx.drawImage(canvas, 0, 0, MODEL_INPUT_SHAPE[2], MODEL_INPUT_SHAPE[3]);
  var imageData = _preprocessCtx.getImageData(0, 0, MODEL_INPUT_SHAPE[2], MODEL_INPUT_SHAPE[3]).data;

  var pixelsPerChannel = MODEL_INPUT_SHAPE[2] * MODEL_INPUT_SHAPE[3];
  var inputData = new Float32Array(3 * pixelsPerChannel);

  for (var y = 0; y < MODEL_INPUT_SHAPE[3]; y++) {
    for (var x = 0; x < MODEL_INPUT_SHAPE[2]; x++) {
      var pixelIdx = (y * MODEL_INPUT_SHAPE[2] + x) * 4;
      for (var c = 0; c < 3; c++) {
        var value = imageData[pixelIdx + c] / 255.0;
        inputData[c * pixelsPerChannel + y * MODEL_INPUT_SHAPE[2] + x] = (value - MEAN[c]) / STD[c];
      }
    }
  }
  return inputData;
}

// ========== Worker 通信 ==========

function sendToWorker(f32) {
  return new Promise(function (resolve) {
    _pendingClassify = resolve;
    _worker.postMessage({ type: 'classify', f32: f32.buffer }, [f32.buffer]);
  });
}

function onWorkerMessage(e) {
  var msg = e.data;
  if (msg.type === 'loaded') {
    _loaded = true;
    _loading = false;
    _loadPromise = null;
    if (_resolveLoad) { _resolveLoad(); _resolveLoad = null; _rejectLoad = null; }
  } else if (msg.type === 'error') {
    _loading = false;
    _loadPromise = null;
    if (_rejectLoad) { _rejectLoad(new Error(msg.error)); _resolveLoad = null; _rejectLoad = null; }
  } else if (msg.type === 'result') {
    if (_pendingClassify) {
      _pendingClassify({ label: msg.label, scores: msg.scores });
      _pendingClassify = null;
    }
  }
}

// ========== 公开 API ==========

export function isLoaded() {
  return _loaded;
}

var _autoInitStarted = false;
var _resolveLoad = null;
var _rejectLoad = null;

export function initDetector() {
  if (_autoInitStarted || _loaded || _loading) return;
  _autoInitStarted = true;
  loadDetector().catch(function () { _autoInitStarted = false; });
}

export function loadDetector() {
  if (_loaded) return Promise.resolve();
  if (_loading && _loadPromise) return _loadPromise;
  _loading = true;

  _loadPromise = new Promise(function (resolve, reject) {
    _resolveLoad = resolve;
    _rejectLoad = reject;

    // 1. 初始化共享 MediaPipe 人脸检测
    var faceReady = getInstance().then(function () {
      log('MediaPipe 人脸检测就绪');
    }).catch(function (err) {
      log('MediaPipe 初始化失败:', err.message || String(err));
      throw err;
    });

    // 2. 初始化 Worker + ONNX 分类模型
    var classifierReady = new Promise(function (res, rej) {
      try {
        _worker = new Worker('./classifierWorker.js');
        _worker.onmessage = onWorkerMessage;
        _worker.onerror = function (err) {
          log('Worker 运行时错误:', err && err.message || String(err));
          rej(new Error('ONNX Worker 运行时错误: ' + (err && err.message || '未知')));
        };

        Promise.all([fetchModelBuffer(), fetchWasmBuffer()]).then(function (results) {
          var modelBuffer = results[0];
          var wasmBuffer = results[1];
          log('发送模型到 Worker, model=' + modelBuffer.byteLength + (wasmBuffer ? ' wasm=' + wasmBuffer.byteLength : ''));
          var transferList = wasmBuffer ? [modelBuffer, wasmBuffer] : [modelBuffer];
          _worker.postMessage(
            { type: 'load', modelBuffer: modelBuffer, wasmBuffer: wasmBuffer },
            transferList
          );
          res();
        }).catch(function (err) {
          log('模型文件加载失败:', err && err.message || String(err));
          log('请检查: 1) ./lib/zhui_an.onnx 是否存在  2) ./lib/ort/ort-wasm.wasm 是否存在');
          rej(err);
        });
      } catch (err) {
        log('Worker 创建失败 (浏览器可能不支持 Web Worker):', err && err.message || String(err));
        rej(err);
      }
    });

    // 两个初始化都完成后等待 Worker 端模型加载
    Promise.all([faceReady, classifierReady]).catch(function (err) {
      _loading = false;
      _loadPromise = null;
      log('初始化失败:', err.message || String(err));
      if (_rejectLoad) { _rejectLoad(err); _resolveLoad = null; _rejectLoad = null; }
    });
  });
  return _loadPromise;
}

// ========== 检测主入口（与 yolo1 完全一致） ==========

/**
 * 裁剪 + 分类（提取公共逻辑）
 * @param {Object} detection - MediaPipe 检测结果（null 表示未检测到）
 * @param {HTMLCanvasElement} fullResSource - 全分辨率源图像
 * @param {number} tTotal - 计时起点
 * @param {boolean} wasCorrected - 是否经过眼角度校正
 * @returns {Promise<{status: number}>}
 */
function doCropAndClassify(detection, fullResSource, tTotal, wasCorrected) {
  if (!detection) {
    log(time() + ' | 未检测到人脸');
    return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });
  }

  var procW = fullResSource.width;
  var procH = fullResSource.height;

  // 裁剪口部区域，裁剪成功后直接送入分类模型，业务结果只看分类标签。
  var cropResult = cropMouthRegion(fullResSource, detection, procW, procH);
  if (!cropResult) {
    log(time() + ' | 裁剪失败（口部超出人脸框或关键点不足）');
    return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });
  }
  if (cropResult.tooFar) {
    log(time() + ' | 距离远 | cropH=' + cropResult.cropH);
    return Promise.resolve({ status: STATUS.TOO_FAR, label: null, scores: null });
  }

  // 预处理 + Worker ONNX 分类
  var f32 = preprocessForClassifier(cropResult.canvas);
  return sendToWorker(f32).then(function (clsResult) {
    var label = clsResult.label;
    var maxScore = Math.max(clsResult.scores['嘴'], clsResult.scores['暗'], clsResult.scores['正常']);
    var total = (performance.now() - tTotal).toFixed(1);

    log(time() + ' | 完成 | 总耗时=' + total + 'ms' +
      ' | cropH=' + cropResult.cropH +
      ' | ' + label +
      ' 嘴=' + (clsResult.scores['嘴'] * 100).toFixed(0) + '%' +
      ' 暗=' + (clsResult.scores['暗'] * 100).toFixed(0) + '%' +
      ' 正常=' + (clsResult.scores['正常'] * 100).toFixed(0) + '%' +
      (wasCorrected ? ' | 已眼校正' : ''));

    // 三类置信度都不超过 20% → 其他物品（与 yolo1 一致）
    if (maxScore <= 0.2) {
      return { status: STATUS.NOT_DETECTED, label: null, scores: null };
    }
    return { status: STATUS.OK, label: label, scores: clsResult.scores };
  });
}

export function detect(source, rotation) {
  if (!_loaded) return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });
  if (_detecting) return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });

  _detecting = true;
  var tTotal = performance.now();

  var sw = source.width || source.videoWidth || 0;
  var sh = source.height || source.videoHeight || 0;
  if (!sw || !sh) {
    _detecting = false;
    return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });
  }

  // 全分辨率画布（用于裁剪精度）
  var fullResSource = prepareFullResInput(source, rotation);
  // 与 yolo1 一致的检测输入
  var detectCanvas = prepareDetectInput(source, rotation);
  if (!detectCanvas) {
    _detecting = false;
    return Promise.resolve({ status: STATUS.NOT_DETECTED, label: null, scores: null });
  }

  // === 第1次人脸检测（与 yolo1 face_pipeline.js:120 一致） ===
  return detectFace(detectCanvas, 5000).then(function (results) {
    if (!results.detections || results.detections.length === 0) {
      log(time() + ' | 未检测到人脸');
      return { status: STATUS.NOT_DETECTED, label: null, scores: null };
    }

    var detection = results.detections[0];

    var procW = fullResSource.width;
    var procH = fullResSource.height;

    // === 眼角度校正（与 yolo1 face_pipeline.js:131-144 一致） ===
    if (detection.landmarks && detection.landmarks.length >= 2) {
      var angle = getEyeAngle(detection.landmarks, procW, procH);
      if (Math.abs(angle) > 15) {
        log(time() + ' | 眼角度=' + angle.toFixed(1) + '°(>15°) 执行校正后重检测');

        // 旋转全分辨率图像
        var correctedSource = rotateImageOnCanvas(fullResSource, -angle);
        // 降采样后二次检测
        var correctedDetectCanvas = prepareDetectInput(correctedSource, null);
        if (!correctedDetectCanvas) {
          return doCropAndClassify(detection, fullResSource, tTotal, false);
        }

        return detectFace(correctedDetectCanvas, 5000).then(function (results2) {
          if (!results2.detections || results2.detections.length === 0) {
            log(time() + ' | 校正后未检测到人脸');
            return { status: STATUS.NOT_DETECTED, label: null, scores: null };
          }
          // 使用校正后的检测结果 + 校正后的全分辨率源
          return doCropAndClassify(results2.detections[0], correctedSource, tTotal, true);
        });
      }
    }

    // 无需校正，直接进入裁剪分类
    return doCropAndClassify(detection, fullResSource, tTotal, false);
  }).catch(function (err) {
    log(time() + ' | 检测异常:', err.message || String(err));
    return { status: STATUS.NOT_DETECTED, label: null, scores: null };
  }).then(function (result) {
    _detecting = false;
    return result;
  });
}
